import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authService } from '../services/auth/auth.service';
import { updateUserPreferences } from '../store/slices/authSlice';
import {
  applyTheme,
  getStoredThemeMode,
  isThemeMode,
  persistThemeModeLocally,
  resolveTheme,
  THEME_MODES,
} from './theme';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [mode, setModeState] = useState(() => getStoredThemeMode());
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(getStoredThemeMode()));
  const syncedUserIdRef = useRef(null);

  const setMode = useCallback(
    async (nextMode, options = {}) => {
      if (!isThemeMode(nextMode)) return;
      persistThemeModeLocally(nextMode);
      setModeState(nextMode);
      const resolved = applyTheme(nextMode, { animate: options.animate !== false });
      setResolvedTheme(resolved);

      if (isAuthenticated && options.persistRemote !== false) {
        try {
          await authService.updatePreferences({ theme: nextMode });
          dispatch(updateUserPreferences({ theme: nextMode }));
        } catch {
          // local preference remains active
        }
      }
    },
    [dispatch, isAuthenticated]
  );

  useEffect(() => {
    const resolved = applyTheme(mode, { animate: false });
    setResolvedTheme(resolved);
  }, [mode]);

  useEffect(() => {
    if (mode !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const resolved = applyTheme('system', { animate: true });
      setResolvedTheme(resolved);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    const serverTheme = user?.preferences?.theme;
    if (!isThemeMode(serverTheme)) return;
    if (syncedUserIdRef.current === user.id && serverTheme === mode) return;

    syncedUserIdRef.current = user.id;
    if (serverTheme !== getStoredThemeMode()) {
      persistThemeModeLocally(serverTheme);
      setModeState(serverTheme);
      setResolvedTheme(resolveTheme(serverTheme));
      applyTheme(serverTheme, { animate: false });
    }
  }, [isAuthenticated, user?.id, user?.preferences?.theme, mode]);

  const value = useMemo(
    () => ({
      mode,
      resolvedTheme,
      setMode,
      modes: THEME_MODES,
    }),
    [mode, resolvedTheme, setMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}
